// @flow
import cn from 'classnames';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';

import Button from '../Button';
import './style.pcss';

export const States = {
  DEFAULT_STATE: 'default',
  SUCCESS_STATE: 'valid',
  INVALID_STATE: 'invalid',
  PROCESS_STATE: 'process',
  SUCCESS_PARSE_STATE: 'success-parsing',
  DISABLED_STATE: 'disabled'
};

export type Props = {|
  additionalClass?: string,
  keyPress?: Function,
  onChange?: Function,
  saveFunction?: (link: string) => Promise<mixed>,
  validate: (value: string) => boolean,
  onFocus?: () => void,
  placeholder?: string,
  value: string,
  defaultHint?: React$Node,
  errorHint?: React$Node,
  label: string,
  initialState?: $Values<typeof States>,
  invalidFunction?: () => void
|}

type State = {|
  value: string,
  wasPasted: boolean,
  inputState: $Values<typeof States>,
  hintMessage: React$Node,
  inputDefaultHint: React$Node,
  inputInvalidHint: React$Node,
  inputErrorHint: React$Node,
|}

class Input extends Component<Props, State> {
  static defaultProps = {
    validate: () => true,
    onFocus: () => {},
    value: ''
  };

  constructor(props) {
    super(props);
    const {
      value,
      defaultHint,
      errorHint,
      initialState
    } = this.props;
    
    let inputState = value ? States.SUCCESS_PARSE_STATE : States.DEFAULT_STATE;
    if (initialState) {
      inputState = initialState;
    }

    this.state = {
      wasPasted: false,
      value,
      inputState,
      // $FlowFixMe TODO убрать inputDefaultHint, inputInvalidHint, inputErrorHint из стейта
      hintMessage: defaultHint,
      // $FlowFixMe
      inputDefaultHint: defaultHint,
      inputInvalidHint: <FormattedMessage id='start_page.url_default.error_input_message' />,
      inputErrorHint: errorHint || <FormattedMessage id='start_page.url_default.success_input_error_data' />
    };
  }

  componentWillReceiveProps(nextProps: Props) {
    const { value } = this.props;

    if (value !== nextProps.value) {
      this.setState({ value: nextProps.value });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { initialState } = this.props;
    const { inputDefaultHint } = this.state;

    if (initialState !== prevProps.initialState) {
      const newState: $Shape<State> = { inputState: initialState };

      if (initialState === States.DISABLED_STATE) {
        newState.value = '';
        newState.hintMessage = inputDefaultHint;
      }

      // Обновляем компонент если получено новое значение initialState сверху
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(newState);
    }
  }

  saveValue = value => {
    const { saveFunction, validate, invalidFunction } = this.props;
    const { inputDefaultHint, inputErrorHint } = this.state;

    if (!validate(value)) {
      this.setState({
        inputState: States.DEFAULT_STATE,
        hintMessage: inputDefaultHint,
        wasPasted: false
      });
    } else if (saveFunction) {
      this.setState({ inputState: States.PROCESS_STATE });
      saveFunction(value).then(result => {
        if (!result) {
          if (invalidFunction) invalidFunction();
          this.setState({
            inputState: States.INVALID_STATE,
            hintMessage: inputErrorHint,
            wasPasted: false
          });
        } else {
          this.setState({
            inputState: States.SUCCESS_PARSE_STATE,
            wasPasted: false
          });
        }
      });
    }
  };

  setCurrentState = value => {
    const { inputDefaultHint, inputInvalidHint, wasPasted } = this.state;
    const { validate, invalidFunction } = this.props;

    if (value === '') {
      this.setState({
        value,
        inputState: States.DEFAULT_STATE,
        hintMessage: inputDefaultHint,
        wasPasted: false
      });
    } else if (validate(value)) {
      this.setState({
        value,
        inputState: States.SUCCESS_STATE,
        hintMessage: null,
        wasPasted: false
      });
      if (wasPasted) {
        this.saveValue(value);
      }
    } else {
      if (invalidFunction) invalidFunction();
      this.setState({
        value,
        inputState: States.INVALID_STATE,
        hintMessage: inputInvalidHint,
        wasPasted: false
      });
    }
  };

  onClickHandler = e => {
    e.preventDefault();
    const { value, inputState, inputDefaultHint } = this.state;
    
    switch (inputState) {
      case States.INVALID_STATE: {
        this.setState({
          value: '',
          inputState: States.DEFAULT_STATE,
          hintMessage: inputDefaultHint,
          wasPasted: false
        });
        break;
      }
      case States.SUCCESS_STATE: {
        this.saveValue(value);
        break;
      }
      default:
    }
  };

  onPasteHandler = event => {
    const clipboardValue = event.clipboardData.getData('Text');
    const { value } = this.state;
    if (value !== clipboardValue) {
      this.setState({ wasPasted: true });
    }
  };

  onKeyPressHandler = e => {
    if (e.key === 'Enter') {
      const { value, inputState } = this.state;
      if (inputState === States.SUCCESS_STATE) {
        this.saveValue(value);
      }
    }
    const { keyPress } = this.props;
    if (keyPress) {
      keyPress();
    }
  };


  handleChange = event => {
    const { onChange } = this.props;

    this.setCurrentState(event.target.value);
    this.setState({ wasPasted: false });
    if (onChange) {
      onChange();
    }
  };

  render() {
    const {
      additionalClass, placeholder, label, onFocus
    } = this.props;
    const {
      inputState, hintMessage, value
    } = this.state;

    return (
      <div
        className={cn(
          'uikit-input-wrapper',
          `uikit-input-wrapper--${inputState}`,
          { 'uikit-input-wrapper--empty-hint': !hintMessage },
          additionalClass,
        )}
        data-id={`input-wrapper-${label}`}
      >
        <input
          value={value}
          onChange={this.handleChange}
          onPaste={this.onPasteHandler}
          onKeyPress={this.onKeyPressHandler}
          onFocus={onFocus}
          className='uikit-input'
          placeholder={placeholder}
          data-state={inputState}
          disabled={[States.PROCESS_STATE, States.DISABLED_STATE].includes(inputState)}
        />
        <Button
          className='uikit-input-button'
          onClick={this.onClickHandler}
        />
        {!!hintMessage && <span className='uikit-input-hint'>{hintMessage}</span>}
      </div>
    );
  }
}

export default injectIntl(Input);
